import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  BreedsClass,
  BreedsTypeClass,
  CommonAutoBrandClass,
  CommonAutoBrandModelClass,
  CommonAutoBrandModelVersionClass,
  CommonAutoBrokenGlassClass,
  CommonAutoCarMaintenance,
  CommonAutoCarReplacement,
  CommonAutoDeductible,
  CommonAutoFuelClass,
  CommonAutoPassengers,
  CommonAutoVehicleSelectionClass,
  CommonAutoVehicleTypeClass,
  CommonAutoVehicleUsageClass,
  CommonBankClass,
  CommonCAEClass,
  CommonDefaultsClass,
  CommonEntityTypeClass,
  CommonFieldTypeClass,
  CommonInterests,
  CommonKeepPriceAsNewClass,
  CommonLiabilityClass,
  CommonMeansAgainstFireClass,
  CommonMeansOfSecurityClass,
  CommonNIFTypeClass,
  CommonPostalAddressResultClass,
  CommonPostalCodeClass,
  CommonSuggestions,
  CommonTypeOfConstructionClass,
  CommonTypeOfHousingClass,
  CommonVesselsDamageClass,
  CommonVesselsHullMaterialClass,
  CommonVesselsNavigationZoneClass,
  CommonVesselsPersonalAccidentsClass,
  CommonVesselsTonnageClass,
  CommonVesselsTypeClass,
  CommumDriveLicenceCategoryClass,
  EmailClass,
  EmailsTemplateClass,
  GenreClass,
  FilesStructureClass,
  MaritalStatusClass,
  ProfessionalsituationClass,
  SexTypesClass,
} from '../models/common-class';
import { CustomerLayoutClass } from '../models/customer-class';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { ValidateFieldMessage } from '../models/validatefield-class';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private lastResults: CommonPostalAddressResultClass[] = [];

  constructor(private http: HttpClient) {}

  searchPostalCode(postalCode: string): Observable<CommonPostalAddressResultClass[]> {
    const normalizedPostalCode = postalCode.replace('-', '').trim();

    if (normalizedPostalCode.length < 4) {
      return of([]);
    }

    return this.http
      .post<
        CommonPostalAddressResultClass[]
      >(`${environment.URL}/common/GetAddressByPostalCodeRS`, { CodigoPostal: normalizedPostalCode })
      .pipe(
        map(results => results.map(r => new CommonPostalAddressResultClass(r))),
        tap(results => (this.lastResults = results)),
        catchError(() => of([])),
      );
  }

  getLastResults(): CommonPostalAddressResultClass[] {
    return this.lastResults;
  }

  /**
   * Obtém moradas por código postal
   * @param CodigoPostal Código postal no formato "XXXX-XXX" ou "XXXXXXX"
   * @returns Observable com array de resultados de morada
   */
  GetAddressByPostalCodeRS(CodigoPostal: string): Observable<CommonPostalAddressResultClass[]> {
    // Normaliza o código postal (remove traços se existirem)
    const normalizedPostalCode = CodigoPostal.replace('-', '');

    return this.http
      .post<
        CommonPostalAddressResultClass[]
      >(`${environment.URL}/common/GetAddressByPostalCodeRS`, { CodigoPostal: normalizedPostalCode })
      .pipe(
        // Mapeia os resultados para instâncias da classe
        map(results => results.map(r => new CommonPostalAddressResultClass(r))),
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  /**
   * Versão simplificada que retorna apenas o primeiro resultado
   * @param CodigoPostal Código postal no formato "XXXX-XXX" ou "XXXXXXX"
   * @returns Observable com o primeiro resultado ou null se não encontrado
   */
  GetFirstAddressByPostalCode(CodigoPostal: string): Observable<CommonPostalAddressResultClass | null> {
    return this.GetAddressByPostalCodeRS(CodigoPostal).pipe(map(results => (results.length > 0 ? results[0] : null)));
  }

  GetCommonDriveLicenceCategory(): Observable<CommumDriveLicenceCategoryClass[]> {
    return this.http
      .get<CommumDriveLicenceCategoryClass[]>(environment.URL + `/common/GetCommonDriveLicenceCategory`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonMeansAgainstFire(idProductsTypes: number): Observable<CommonMeansAgainstFireClass[]> {
    return this.http
      .post<CommonMeansAgainstFireClass[]>(environment.URL + `/common/GetCommonMeansAgainstFire`, { idProductsTypes })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonTypeOfConstruction(idProductsTypes: number): Observable<CommonTypeOfConstructionClass[]> {
    return this.http
      .post<
        CommonTypeOfConstructionClass[]
      >(environment.URL + `/common/GetCommonTypeOfConstruction`, { idProductsTypes })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonTypeOfHousing(idProductsTypes: number): Observable<CommonTypeOfHousingClass[]> {
    return this.http
      .post<CommonTypeOfHousingClass[]>(environment.URL + `/common/GetCommonTypeOfHousing`, { idProductsTypes })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonMeansOfSecurity(idProductsTypes: number): Observable<CommonMeansOfSecurityClass[]> {
    return this.http
      .post<CommonMeansOfSecurityClass[]>(environment.URL + `/common/GetCommonMeansOfSecurity`, { idProductsTypes })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetAPPVersion(): Observable<string[]> {
    return this.http.get<string[]>(environment.URL + `/common/GetAPPVersion`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SetCommonInterests(commonInterests: CommonInterests, uuid: string) {
    commonInterests.UUIDLastChange = uuid;

    return this.http.post(environment.URL + `/common/SetCommonInterests`, commonInterests).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonInterests(): Observable<CommonInterests[]> {
    return this.http.get<CommonInterests[]>(environment.URL + `/common/GetCommonInterests`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ClientAreaSetCustomerLayoutClientArea(customerLayoutClass: CustomerLayoutClass, uuid: string) {
    customerLayoutClass.UUIDLastChange = uuid;

    return this.http.post(environment.URL + `/common/ClientAreaSetCustomerLayoutClientArea`, customerLayoutClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoCarMaintenance(): Observable<CommonAutoCarMaintenance[]> {
    return this.http.get<CommonAutoCarMaintenance[]>(environment.URL + `/common/GetCommonAutoCarMaintenance`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoCarReplacement(): Observable<CommonAutoCarReplacement[]> {
    return this.http.get<CommonAutoCarReplacement[]>(environment.URL + `/common/GetCommonAutoCarReplacement`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoPassengers(): Observable<CommonAutoPassengers[]> {
    return this.http.get<CommonAutoPassengers[]>(environment.URL + `/common/GetCommonAutoPassengers`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoDeductible(): Observable<CommonAutoDeductible[]> {
    return this.http.get<CommonAutoDeductible[]>(environment.URL + `/common/GetCommonAutoDeductible`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoBrokenGlass(): Observable<CommonAutoBrokenGlassClass[]> {
    return this.http.get<CommonAutoBrokenGlassClass[]>(environment.URL + `/common/GetCommonAutoBrokenGlass`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonMyMatriculas(idCustomer: number): Observable<string[]> {
    return this.http.post<string[]>(environment.URL + `/common/GetCommonMyMatriculas`, { idCustomer }).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoLiability(): Observable<CommonLiabilityClass[]> {
    return this.http.get<CommonLiabilityClass[]>(environment.URL + `/common/GetCommonAutoLiability`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoVehicleKeepPriceAsNew(): Observable<CommonKeepPriceAsNewClass[]> {
    return this.http
      .get<CommonKeepPriceAsNewClass[]>(environment.URL + `/common/GetCommonAutoVehicleKeepPriceAsNew`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonEntityType(): Observable<CommonEntityTypeClass[]> {
    return this.http.get<CommonEntityTypeClass[]>(environment.URL + `/common/GetCommonEntityType`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ManagementSetCommonAutoBrandModelVersion(
    commonAutoBrandModelVersionClass: CommonAutoBrandModelVersionClass,
    uuid: string,
  ) {
    commonAutoBrandModelVersionClass.UUIDLastChange = uuid;

    return this.http
      .post(environment.URL + `/common/ManagementSetCommonAutoBrandModelVersion`, commonAutoBrandModelVersionClass)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonAutoBrandModelVersion(
    idCommonAutoBrand: number,
    idCommonAutoBrandModel: number,
  ): Observable<CommonAutoBrandModelVersionClass[]> {
    return this.http
      .post<
        CommonAutoBrandModelVersionClass[]
      >(environment.URL + `/common/GetCommonAutoBrandModelVersion`, { idCommonAutoBrand, idCommonAutoBrandModel })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetCommonAutoBrandModel(commonAutoBrandModelClass: CommonAutoBrandModelClass, uuid: string) {
    commonAutoBrandModelClass.UUIDLastChange = uuid;

    return this.http
      .post(environment.URL + `/common/ManagementSetCommonAutoBrandModel`, commonAutoBrandModelClass)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonAutoBrandModel(idCommonAutoBrand: number): Observable<CommonAutoBrandModelClass[]> {
    return this.http
      .post<CommonAutoBrandModelClass[]>(environment.URL + `/common/GetCommonAutoBrandModel`, { idCommonAutoBrand })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetCommonAutoBrand(commonAutoBrandClass: CommonAutoBrandClass, uuid: string) {
    commonAutoBrandClass.UUIDLastChange = uuid;

    return this.http.post(environment.URL + `/common/ManagementSetCommonAutoBrand`, commonAutoBrandClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoBrand(): Observable<CommonAutoBrandClass[]> {
    return this.http.get<CommonAutoBrandClass[]>(environment.URL + `/common/GetCommonAutoBrand/`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonAutoFuel(idProductsTypes: number): Observable<CommonAutoFuelClass[]> {
    return this.http
      .post<CommonAutoFuelClass[]>(environment.URL + `/common/GetCommonAutoFuel`, { idProductsTypes })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonAutoVehicleSelection(): Observable<CommonAutoVehicleSelectionClass[]> {
    return this.http
      .get<CommonAutoVehicleSelectionClass[]>(environment.URL + `/common/GetCommonAutoVehicleSelection/`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonAutoVehicleUsage(idCommonAutoVehicleType: number): Observable<CommonAutoVehicleUsageClass[]> {
    return this.http
      .post<
        CommonAutoVehicleUsageClass[]
      >(environment.URL + `/common/GetCommonAutoVehicleUsage`, { idCommonAutoVehicleType })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonAutoVehicleType(idProductsTypes: number): Observable<CommonAutoVehicleTypeClass[]> {
    return this.http
      .post<CommonAutoVehicleTypeClass[]>(environment.URL + `/common/GetCommonAutoVehicleType`, { idProductsTypes })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonVesselsPersonalAccidents(): Observable<CommonVesselsPersonalAccidentsClass[]> {
    return this.http
      .get<CommonVesselsPersonalAccidentsClass[]>(environment.URL + `/common/GetCommonVesselsPersonalAccidents`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonFieldType(): Observable<CommonFieldTypeClass[]> {
    return this.http.get<CommonFieldTypeClass[]>(environment.URL + `/common/GetCommonFieldType`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ManagementSetCommonFieldType(commonFieldTypeClass: CommonFieldTypeClass, uuid: string) {
    commonFieldTypeClass.UUIDLastChange = uuid;

    return this.http.post(environment.URL + `/common/ManagementSetCommonFieldType`, commonFieldTypeClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetFilesStructure(): Observable<FilesStructureClass[]> {
    return this.http.get<FilesStructureClass[]>(environment.URL + `/common/GetFilesStructure`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ManagementSetFilesStructure(filesStructureClass: FilesStructureClass, uuid: string) {
    filesStructureClass.UUIDLastChange = uuid;

    return this.http.post(environment.URL + `/common/ManagementSetFilesStructure`, filesStructureClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonVesselsType(idProductsTypes: number): Observable<CommonVesselsTypeClass[]> {
    return this.http
      .post<CommonVesselsTypeClass[]>(environment.URL + `/common/GetCommonVesselsType`, { idProductsTypes })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonVesselsDamage(): Observable<CommonVesselsDamageClass[]> {
    return this.http.get<CommonVesselsDamageClass[]>(environment.URL + `/common/GetCommonVesselsDamage`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonVesselsHullMaterial(idCommonVesselsType: number): Observable<CommonVesselsHullMaterialClass[]> {
    return this.http
      .post<
        CommonVesselsHullMaterialClass[]
      >(environment.URL + `/common/GetCommonVesselsHullMaterial`, { idCommonVesselsType })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonVesselsNavigationZone(idCommonVesselsType: number): Observable<CommonVesselsNavigationZoneClass[]> {
    return this.http
      .post<
        CommonVesselsNavigationZoneClass[]
      >(environment.URL + `/common/GetCommonVesselsNavigationZone`, { idCommonVesselsType })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonVesselsTonnage(): Observable<CommonVesselsTonnageClass[]> {
    return this.http.get<CommonVesselsTonnageClass[]>(environment.URL + `/common/GetCommonVesselsTonnage`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  CommonCodigospostaisFindList(Text: string): Observable<CommonPostalCodeClass[]> {
    return this.http
      .post<CommonPostalCodeClass[]>(environment.URL + `/common/CommonCodigospostaisFindList`, { Text })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetCommonBankCod(Cod: string): Observable<CommonBankClass> {
    return this.http.post<CommonBankClass>(environment.URL + `/common/GetCommonBankCod`, { Cod }).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonBanks(): Observable<CommonBankClass[]> {
    return this.http.post<CommonBankClass[]>(environment.URL + `/common/GetCommonBanks`, {}).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  CommonSuggestionsSet(commonSuggestions: CommonSuggestions, uuid: string) {
    commonSuggestions.UUIDLastChange = uuid;

    return this.http.post(environment.URL + `/common/CommonSuggestionsSet`, commonSuggestions).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  CommonSuggestionsGet(idCustomer: number): Observable<CommonSuggestions[]> {
    return this.http.post<CommonSuggestions[]>(environment.URL + `/common/CommonSuggestionsGet`, { idCustomer }).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  CommonSuggestionsGetList(): Observable<CommonSuggestions[]> {
    return this.http.get<CommonSuggestions[]>(environment.URL + `/common/CommonSuggestionsGetList`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonCAE(CAE: string): Observable<CommonCAEClass> {
    return this.http.post<CommonCAEClass>(environment.URL + `/common/GetCommonCAE`, { CAE }).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonCAEALL(): Observable<CommonCAEClass[]> {
    return this.http.get<CommonCAEClass[]>(environment.URL + `/common/GetCommonCAEALL`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonDefaults(): Observable<CommonDefaultsClass> {
    return this.http.get<CommonDefaultsClass>(environment.URL + `/common/GetCommonDefaults`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SetCommonDefault(funcionalidade: string, valor: any, idUser: number) {
    const body = {
      Funcionalidade: funcionalidade,
      Valor: valor,
      idUser: idUser,
    };

    return this.http.post(environment.URL + `/common/SetCommonDefault`, body).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SetCommonParameter(funcionalidade: string, valor: any) {
    const body = {
      Funcionalidade: funcionalidade,
      Valor: valor,
    };

    return this.http.post(environment.URL + `/common/SetCommonDefault`, body).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonBreedsType(): Observable<BreedsTypeClass[]> {
    return this.http.get<BreedsTypeClass[]>(environment.URL + `/common/GetCommonBreedsType`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonBreeds(idProductsTypes: number): Observable<BreedsClass[]> {
    return this.http.get<BreedsClass[]>(environment.URL + `/common/GetCommonBreeds/${idProductsTypes}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonGenre(): Observable<GenreClass[]> {
    return this.http.get<GenreClass[]>(environment.URL + `/common/GetCommonGenre`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SendEmailWelcome(emailClass: EmailClass) {
    return this.http.post(environment.URL + `/common/SendEmailWelcome`, emailClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SendEmailRestorePassword(emailClass: EmailClass) {
    return this.http.post(environment.URL + `/common/SendEmailRestorePassword`, emailClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SendEmailMarketing(emailClass: EmailClass) {
    return this.http.post(environment.URL + `/common/SendEmailMarketing`, emailClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SendEmailSinistros(emailClass: EmailClass) {
    return this.http.post(environment.URL + `/common/SendEmailSinistros`, emailClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SendEmail(emailClass: EmailClass) {
    return this.http.post(environment.URL + `/common/SendEmail`, emailClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  SendLog(emailClass: EmailClass) {
    return this.http.post(environment.URL + `/common/SendLog`, emailClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonSexTypes(): Observable<SexTypesClass[]> {
    return this.http.get<SexTypesClass[]>(environment.URL + `/common/GetCommonSexTypes`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetEmailstemplates(): Observable<EmailsTemplateClass[]> {
    return this.http.get<EmailsTemplateClass[]>(environment.URL + `/common/GetManagementEmailstemplates`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  ManagementSetEmailstemplates(emailsTemplateClass: EmailsTemplateClass, uuid: string) {
    emailsTemplateClass.UUIDLastChange = uuid;

    return this.http.post(environment.URL + `/common/ManagementSetEmailstemplates`, emailsTemplateClass).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // tslint:disable-next-line:variable-name
  SetSiteRoutes(_description: string, _url: string, uuid: string): Observable<any> {
    const dados = {
      Description: _description,
      URL: _url,
      UUIDLastChange: uuid,
    };
    // tslint:disable-next-line:object-literal-shorthand
    return this.http.post(environment.URL + `/common/SetsiteRoutes`, dados).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // tslint:disable-next-line:variable-name
  SetSiteRoutesLog(_idUser: number, _description: string, _url: string, uuid: string): Observable<any> {
    const dados = {
      idUser: _idUser,
      Description: _description,
      URL: _url,
      UUIDLastChange: uuid,
    };

    // tslint:disable-next-line:object-literal-shorthand
    return this.http.post(environment.URL + `/common/SetsiteRoutesLog`, dados).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetMaritalStatus(): Observable<MaritalStatusClass[]> {
    return this.http.get<MaritalStatusClass[]>(environment.URL + `/common/GetMaritalStatus`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetProfessionalsituationClass(): Observable<ProfessionalsituationClass[]> {
    return this.http.get<ProfessionalsituationClass[]>(environment.URL + `/common/GetProfessionalsituation`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetCommonNIFType(): Observable<CommonNIFTypeClass[]> {
    return this.http.get<CommonNIFTypeClass[]>(environment.URL + `/common/GetCommonNIFType`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetValidateFieldMessageByField(Field: string): Observable<ValidateFieldMessage[]> {
    return this.http
      .get<ValidateFieldMessage[]>(environment.URL + `/common/GetValidateFieldMessageByField/${Field}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetValidateFieldMessagesALL(): Observable<ValidateFieldMessage[]> {
    return this.http.get<ValidateFieldMessage[]>(environment.URL + `/common/GetValidateFieldMessagesALL`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }
  GetInitialData(): Observable<any> {
    return this.http.get<any>(environment.URL + `/common/GetInitialData`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }
  GetHomepageActiveLayout(): Observable<any> {
    return this.http.get<any>(environment.URL + `/homepage-layout/active`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  UpdateHomepageLayout(id: number, layoutJSON: any): Observable<any> {
    return this.http.put<any>(environment.URL + `/homepage-layout/${id}`, { LayoutJSON: layoutJSON }).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }
}
